import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Card, Checkbox, Modal, Space, Switch, Tooltip, Typography, notification,
} from 'antd';
import { PlayCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { REGION_OPTIONS } from '../constants';
import useCreateSegmentationRunsMutation from './use-create-segmentation-runs-mutation';

const { Text, Paragraph } = Typography;

const TriggerRunCard = ({ onRunsCreated }) => {
  const [regions, setRegions] = useState(['NZ', 'AU']);
  const [requireApproval, setRequireApproval] = useState(true);

  const queryClient = useQueryClient();

  const { mutate: createRuns, isLoading } = useCreateSegmentationRunsMutation({
    onSuccess: (data) => {
      const created = data?.runs ?? [];
      notification.success({
        message: created.length > 1 ? 'Segmentation runs started' : 'Segmentation run started',
        description: requireApproval
          ? 'Scoring has begun. You will be able to review the segment spread before applying.'
          : 'Unattended run started — changes will apply automatically once scored.',
      });
      queryClient.invalidateQueries(['segmentation', 'runs']);
      if (created.length) onRunsCreated(created);
    },
  });

  const startRun = () => {
    const autoApply = !requireApproval;
    createRuns({ regions, autoApply });
  };

  const handleStart = () => {
    if (!regions.length) return;
    if (requireApproval) {
      startRun();
      return;
    }
    // Auto-apply skips the human check and writes straight to Cin7 + Monday.
    Modal.confirm({
      title: 'Start an unattended auto-apply run?',
      icon: <ThunderboltOutlined style={{ color: '#fa8c16' }} />,
      content: (
        <span>
          Approval is off, so this run will apply segment changes to Cin7 and Monday
          automatically as soon as scoring completes — with no human check of the
          distribution. Continue?
        </span>
      ),
      okText: 'Start auto-apply run',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: startRun,
    });
  };

  return (
    <Card
      className="segmentation-section"
      title={(
        <Space>
          <PlayCircleOutlined />
          Start a segmentation run
        </Space>
      )}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text strong>Regions</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Select one or both regions. Each region runs as its own independent run.
          </Paragraph>
          <Checkbox.Group
            options={REGION_OPTIONS}
            value={regions}
            onChange={setRegions}
          />
        </div>

        <div className="segmentation-approval-toggle">
          <div>
            <Space size="small">
              <Switch checked={requireApproval} onChange={setRequireApproval} />
              <Text strong>Require approval</Text>
            </Space>
            <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
              {requireApproval
                ? 'You will review the segment distribution and approve before anything is written to Cin7 or Monday.'
                : 'Unattended: the run applies changes automatically once scored (used for overnight scheduled runs).'}
            </Paragraph>
          </div>
        </div>

        {!requireApproval && (
          <Alert
            type="warning"
            showIcon
            message="Auto-apply is on — this run will write to Cin7 and Monday without a human check."
          />
        )}

        <div>
          <Tooltip title={regions.length ? undefined : 'Select at least one region'}>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={isLoading}
              disabled={!regions.length}
              onClick={handleStart}
            >
              Start run
            </Button>
          </Tooltip>
        </div>
      </Space>
    </Card>
  );
};

TriggerRunCard.propTypes = {
  onRunsCreated: PropTypes.func,
};

TriggerRunCard.defaultProps = {
  onRunsCreated: () => {},
};

export default TriggerRunCard;
